import type { OfficeEdit, OfficeTextFormat } from "@nevisign/reader-core";
import { resolveApplyMode } from "@nevisign/edit-ops";
import { appendLog } from "./logger";

function applyFormatToElement(el: HTMLElement, format?: OfficeTextFormat): void {
  if (!format) return;
  if (format.bold) el.style.fontWeight = "700";
  if (format.italic) el.style.fontStyle = "italic";
  if (format.underline) el.style.textDecoration = "underline";
  if (format.color) el.style.color = format.color;
  if (format.highlight) el.style.backgroundColor = format.highlight;
  if (format.alignment) {
    const map: Record<string, string> = {
      Left: "left",
      Right: "right",
      Centered: "center",
      Justified: "justify",
    };
    const p = el.closest(".nr-p") as HTMLElement | null;
    if (p) p.style.textAlign = map[format.alignment] ?? "right";
  }
}

/**
 * Replace every occurrence of `find` under `root` (not just the first).
 * Walks text nodes; restarts after each mutation so the tree stays valid.
 */
function replaceTextInRoot(
  root: HTMLElement,
  find: string,
  replace: string,
  format?: OfficeTextFormat,
  opts?: { all?: boolean; max?: number }
): number {
  if (!find) return 0;
  const all = opts?.all !== false;
  const max = opts?.max ?? (all ? 500 : 1);
  let count = 0;

  while (count < max) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node: Text | null;
    let hit: { node: Text; idx: number } | null = null;
    while ((node = walker.nextNode() as Text | null)) {
      const idx = node.data.indexOf(find);
      if (idx < 0) continue;
      hit = { node, idx };
      break;
    }
    if (!hit) break;

    const { node: textNode, idx } = hit;
    const before = textNode.data.slice(0, idx);
    const after = textNode.data.slice(idx + find.length);
    const parent = textNode.parentElement;
    if (!parent) break;

    const span = document.createElement("span");
    span.className = "nr-ai-edit";
    span.textContent = replace;
    applyFormatToElement(span, format);

    const frag = document.createDocumentFragment();
    if (before) frag.appendChild(document.createTextNode(before));
    frag.appendChild(span);
    if (after) frag.appendChild(document.createTextNode(after));
    parent.replaceChild(frag, textNode);
    count += 1;
    if (!all) break;
  }

  return count;
}

function replaceSelection(
  replace: string,
  format?: OfficeTextFormat
): boolean {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return false;
  const range = sel.getRangeAt(0);
  range.deleteContents();
  const span = document.createElement("span");
  span.className = "nr-ai-edit";
  span.textContent = replace;
  applyFormatToElement(span, format);
  range.insertNode(span);
  sel.removeAllRanges();
  return true;
}

function formatAllOccurrences(
  root: HTMLElement,
  find: string,
  format?: OfficeTextFormat
): number {
  return replaceTextInRoot(root, find, find, format, { all: true });
}

/**
 * Best-effort apply office assistant edits into DOCX contentEditable.
 *
 * - Normal (no selection scope): replace/format ALL occurrences in the document.
 * - Selection-scoped: prefer live selection; fall back to quote; still apply
 *   whole-doc for plain `replace`/`delete`/`format` with an explicit `find`.
 */
export function applyOfficeEditsToDom(
  root: HTMLElement | null,
  edits: OfficeEdit[],
  selectionQuote?: string,
  opts?: { scopeToSelection?: boolean }
): { applied: number; failed: number; occurrenceCount: number } {
  if (!root || !edits.length) {
    return { applied: 0, failed: 0, occurrenceCount: 0 };
  }
  let applied = 0;
  let failed = 0;
  let occurrenceCount = 0;
  const scopeToSelection = Boolean(
    opts?.scopeToSelection && (selectionQuote?.trim() || hasLiveSelection())
  );

  for (const edit of edits) {
    try {
      let ok = false;
      let hits = 0;
      const mode = resolveApplyMode(edit, {
        hasSelectionScope: scopeToSelection,
      });
      const applyAll = mode === "all";

      if (edit.type === "replace") {
        if (!edit.find && scopeToSelection) {
          ok = replaceSelection(edit.replace, edit.format);
          if (!ok && selectionQuote) {
            hits = replaceTextInRoot(
              root,
              selectionQuote,
              edit.replace,
              edit.format,
              { all: false }
            );
            ok = hits > 0;
          }
        } else {
          hits = replaceTextInRoot(root, edit.find, edit.replace, edit.format, {
            all: applyAll,
          });
          ok = hits > 0;
        }
      } else if (edit.type === "replace_selection") {
        ok = replaceSelection(edit.replace, edit.format);
        if (!ok && selectionQuote) {
          hits = replaceTextInRoot(
            root,
            selectionQuote,
            edit.replace,
            edit.format,
            { all: false }
          );
          ok = hits > 0;
        }
      } else if (edit.type === "insert") {
        if (edit.at === "selection" || !edit.at) {
          ok = replaceSelection(edit.insert, edit.format);
        } else if (edit.at === "end") {
          const p = document.createElement("p");
          p.className = "nr-p nr-ai-edit";
          p.textContent = edit.insert;
          applyFormatToElement(p, edit.format);
          root.appendChild(p);
          ok = true;
        } else if (edit.at === "start") {
          const p = document.createElement("p");
          p.className = "nr-p nr-ai-edit";
          p.textContent = edit.insert;
          applyFormatToElement(p, edit.format);
          root.insertBefore(p, root.firstChild);
          ok = true;
        }
      } else if (edit.type === "insert_after") {
        const anchor = edit.anchor_find || selectionQuote || "";
        if (anchor) {
          hits = replaceTextInRoot(
            root,
            anchor,
            `${anchor}${edit.content.startsWith("\n") ? "" : "\n"}${edit.content}`,
            edit.format,
            { all: false }
          );
          ok = hits > 0;
        } else {
          const p = document.createElement("p");
          p.className = "nr-p nr-ai-edit";
          p.textContent = edit.content;
          applyFormatToElement(p, edit.format);
          root.appendChild(p);
          ok = true;
        }
      } else if (edit.type === "delete") {
        hits = replaceTextInRoot(root, edit.find, "", undefined, {
          all: applyAll,
        });
        ok = hits > 0;
      } else if (edit.type === "format") {
        const targetSelection =
          mode === "selection" ||
          edit.target === "selection" ||
          (!edit.find && scopeToSelection) ||
          (!edit.find && Boolean(selectionQuote));

        if (targetSelection) {
          const sel = window.getSelection();
          if (sel && sel.rangeCount && !sel.isCollapsed) {
            const range = sel.getRangeAt(0);
            const span = document.createElement("span");
            span.className = "nr-ai-edit";
            applyFormatToElement(span, edit.format);
            try {
              range.surroundContents(span);
              ok = true;
              hits = 1;
            } catch {
              const needle = edit.find || selectionQuote || "";
              if (needle) {
                hits = applyAll
                  ? formatAllOccurrences(root, needle, edit.format)
                  : replaceTextInRoot(root, needle, needle, edit.format, {
                      all: false,
                    });
                ok = hits > 0;
              }
            }
          } else if (selectionQuote || edit.find) {
            const needle = edit.find || selectionQuote!;
            hits = applyAll
              ? formatAllOccurrences(root, needle, edit.format)
              : replaceTextInRoot(root, needle, needle, edit.format, {
                  all: false,
                });
            ok = hits > 0;
          }
        } else if (edit.find) {
          hits = applyAll
            ? formatAllOccurrences(root, edit.find, edit.format)
            : replaceTextInRoot(root, edit.find, edit.find, edit.format, {
                all: false,
              });
          ok = hits > 0;
        }
      } else if (edit.type === "comment") {
        // DOM reader: mark with a title attribute span (best-effort)
        const needle = edit.find || selectionQuote || "";
        if (needle) {
          hits = replaceTextInRoot(root, needle, needle, undefined, {
            all: false,
          });
          ok = hits > 0;
          appendLog("info", "assistant-edit", `comment noted: ${edit.comment.slice(0, 80)}`);
        }
      }

      occurrenceCount += hits || (ok ? 1 : 0);
      if (ok) applied += 1;
      else failed += 1;
    } catch (err) {
      failed += 1;
      appendLog("warn", "assistant-edit", "apply failed", err);
    }
  }

  appendLog(
    "info",
    "assistant-edit",
    `applied ${applied}, failed ${failed}, occurrences ${occurrenceCount}`,
    { scopeToSelection }
  );
  return { applied, failed, occurrenceCount };
}

function hasLiveSelection(): boolean {
  const sel = window.getSelection();
  return Boolean(sel && sel.rangeCount > 0 && !sel.isCollapsed);
}

/** Collect plain text from open DOCX viewer (preferred live DOM). */
export function extractLiveDocxText(root: HTMLElement | null): string {
  if (!root) return "";
  return (root.innerText || root.textContent || "").trim();
}
